import React, { memo, useEffect } from "react";
import { Platform, StyleSheet, View } from "react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
  interpolate,
  Extrapolation,
} from "react-native-reanimated";
import { SvgUri } from "react-native-svg";
import { Image as RNImage } from "react-native";

// Icon URLs
const VDB_TOP_ICON =
  "https://cdn.vdbapp.com/ai/chat-widget/assets/img/logo-top.svg";
const VDB_BOTTOM_ICON =
  "https://cdn.vdbapp.com/ai/chat-widget/assets/img/logo-bottom.svg";
const VDB_LEFT_ICON =
  "https://cdn.vdbapp.com/ai/chat-widget/assets/img/logo-left.svg";
const VDB_RIGHT_ICON =
  "https://cdn.vdbapp.com/ai/chat-widget/assets/img/logo-right.svg";
const VDB_ICONS = [
  VDB_TOP_ICON,
  VDB_BOTTOM_ICON,
  VDB_LEFT_ICON,
  VDB_RIGHT_ICON,
];

// Constants
const LOGO_SIZE = 32;
const ICON_PART_SIZE = 32;
const BRIGHT_OPACITY = 1;
const DIM_OPACITY = 0.3;
const ICON_ANIMATION_DURATION = 2500;

const isWeb = Platform.OS === "web";

// Preload SVG icons on module load for faster display
if (isWeb && typeof document !== "undefined") {
  VDB_ICONS.forEach((uri) => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = uri;
    document.head.appendChild(link);
  });

  // Inject CSS animations for web
  const styleId = "animated-vdb-logo-styles";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes iconPulseTop {
        0% { opacity: 0.3; }
        25% { opacity: 0.6; }
        50% { opacity: 1; }
        75% { opacity: 0.6; }
        100% { opacity: 0.3; }
      }
      @keyframes iconPulseRight {
        0% { opacity: 0.6; }
        25% { opacity: 0.3; }
        50% { opacity: 0.6; }
        75% { opacity: 1; }
        100% { opacity: 0.6; }
      }
      @keyframes iconPulseBottom {
        0% { opacity: 1; }
        25% { opacity: 0.6; }
        50% { opacity: 0.3; }
        75% { opacity: 0.6; }
        100% { opacity: 1; }
      }
      @keyframes iconPulseLeft {
        0% { opacity: 0.6; }
        25% { opacity: 1; }
        50% { opacity: 0.6; }
        75% { opacity: 0.3; }
        100% { opacity: 0.6; }
      }
      .vdb-icon-top { animation: iconPulseTop 2.5s linear infinite; }
      .vdb-icon-right { animation: iconPulseRight 2.5s linear infinite; }
      .vdb-icon-bottom { animation: iconPulseBottom 2.5s linear infinite; }
      .vdb-icon-left { animation: iconPulseLeft 2.5s linear infinite; }
    `;
    document.head.appendChild(style);
  }
} else {
  // Preload for native - prefetch the images
  VDB_ICONS.forEach((uri) => {
    RNImage.prefetch(uri).catch(() => {
      // Silently ignore prefetch errors
    });
  });
}

// Static icon style for web - defined outside component to prevent recreation
const webIconStyle = { width: ICON_PART_SIZE, height: ICON_PART_SIZE } as const;

// Helper function to calculate opacity based on rotation progress
// Smooth continuous loader-like animation - dimness flows around the circle
const getIconOpacity = (progress: number, iconPhase: number): number => {
  "worklet";
  // iconPhase: 0 = top, 0.25 = right, 0.5 = bottom, 0.75 = left

  // Calculate circular distance from this icon's phase
  let distance = progress - iconPhase;
  if (distance < 0) distance += 1;
  if (distance > 0.5) distance = 1 - distance;

  // Smooth sine-based interpolation for continuous flow
  // Icon is dimmest when distance is 0, brightest when distance is 0.5
  const smoothProgress = Math.cos(distance * Math.PI * 2) * 0.5 + 0.5;

  return interpolate(
    smoothProgress,
    [0, 1],
    [BRIGHT_OPACITY, DIM_OPACITY],
    Extrapolation.CLAMP
  );
};

// Web Icon Component - memoized to prevent re-renders
const WebIcon = memo(
  ({
    uri,
    style,
    className,
  }: {
    uri: string;
    style: any;
    className: string;
  }) => (
    <View style={style}>
      {/* @ts-ignore - using img tag for web */}
      <img src={uri} className={className} style={webIconStyle} alt="" />
    </View>
  )
);

// Native Icon Component - memoized SVG wrapper
const NativeIcon = memo(({ uri }: { uri: string }) => (
  <SvgUri uri={uri} width={ICON_PART_SIZE} height={ICON_PART_SIZE} />
));

export type AnimatedVDBLogoProps = {
  size?: number;
};

/**
 * Animated VDB Logo with circular opacity animation
 * All animations run on UI thread via Reanimated worklets - no JS bridge overhead
 */
const AnimatedVDBLogo = memo(({ size }: AnimatedVDBLogoProps = {}) => {
  // Shared value for animation progress - runs entirely on UI thread
  const iconProgress = useSharedValue(0);

  // Start animation once on mount - no dependencies to prevent restarts
  useEffect(() => {
    if (!isWeb) {
      iconProgress.value = withRepeat(
        withTiming(1, {
          duration: ICON_ANIMATION_DURATION,
          easing: Easing.linear,
        }),
        -1,
        false
      );
    }
  }, []); // Empty deps - animation starts once and runs forever

  // All opacity calculations run on UI thread as worklets
  const topStyle = useAnimatedStyle(() => {
    "worklet";
    return { opacity: getIconOpacity(iconProgress.value, 0) };
  });

  const rightStyle = useAnimatedStyle(() => {
    "worklet";
    return { opacity: getIconOpacity(iconProgress.value, 0.25) };
  });

  const bottomStyle = useAnimatedStyle(() => {
    "worklet";
    return { opacity: getIconOpacity(iconProgress.value, 0.5) };
  });

  const leftStyle = useAnimatedStyle(() => {
    "worklet";
    return { opacity: getIconOpacity(iconProgress.value, 0.75) };
  });

  const containerStyle = size ? { width: size, height: size } : undefined;

  // Web version - CSS handles animations, no JS overhead
  if (isWeb) {
    return (
      <View style={[styles.vdbLogoContainer, containerStyle]}>
        <WebIcon
          uri={VDB_TOP_ICON}
          style={styles.vdbIconTop}
          className="vdb-icon-top"
        />
        <WebIcon
          uri={VDB_RIGHT_ICON}
          style={styles.vdbIconRight}
          className="vdb-icon-right"
        />
        <WebIcon
          uri={VDB_BOTTOM_ICON}
          style={styles.vdbIconBottom}
          className="vdb-icon-bottom"
        />
        <WebIcon
          uri={VDB_LEFT_ICON}
          style={styles.vdbIconLeft}
          className="vdb-icon-left"
        />
      </View>
    );
  }

  // Native version - Reanimated handles animations on UI thread
  return (
    <View style={[styles.vdbLogoContainer, containerStyle]}>
      <Animated.View style={[styles.vdbIconTop, topStyle]}>
        <NativeIcon uri={VDB_TOP_ICON} />
      </Animated.View>
      <Animated.View style={[styles.vdbIconRight, rightStyle]}>
        <NativeIcon uri={VDB_RIGHT_ICON} />
      </Animated.View>
      <Animated.View style={[styles.vdbIconBottom, bottomStyle]}>
        <NativeIcon uri={VDB_BOTTOM_ICON} />
      </Animated.View>
      <Animated.View style={[styles.vdbIconLeft, leftStyle]}>
        <NativeIcon uri={VDB_LEFT_ICON} />
      </Animated.View>
    </View>
  );
});

const styles = StyleSheet.create({
  vdbLogoContainer: {
    width: LOGO_SIZE,
    height: LOGO_SIZE,
    position: "relative",
    alignItems: "center",
    justifyContent: "center",
  },
  vdbIconTop: {
    position: "absolute",
    top: 0,
    alignSelf: "center",
  },
  vdbIconRight: {
    position: "absolute",
    right: 0,
    alignSelf: "center",
  },
  vdbIconBottom: {
    position: "absolute",
    bottom: 0,
    alignSelf: "center",
  },
  vdbIconLeft: {
    position: "absolute",
    left: 0,
    alignSelf: "center",
  },
});

export default AnimatedVDBLogo;
